// src/modules/PackageManager/UploadDiscoveryScriptModal.tsx
import React, { useEffect, useState } from 'react';
import { X, Upload, FileCode } from 'lucide-react';

interface UploadDiscoveryScriptModalProps {
  show: boolean;
  packageName: string | null;
  packageIdentifier: string | null;
  uploading: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<void>;
}

const UploadDiscoveryScriptModal: React.FC<UploadDiscoveryScriptModalProps> = ({
  show,
  packageName,
  packageIdentifier,
  uploading,
  onClose,
  onUpload,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    if (show) {
      setSelectedFile(null);
    }
  }, [show]);

  if (!show) return null;

  const handleUpload = async () => {
    if (!selectedFile) return;
    await onUpload(selectedFile);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm transition-opacity animate-fade-in">
      <div className="bg-gradient-to-br from-[#1c1c1c] to-[#2a2a2a] text-white rounded-2xl shadow-2xl p-6 w-[500px] max-h-[85vh] overflow-hidden transform transition-all duration-300 scale-100 animate-slide-up border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-700 pb-4 mb-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-900/20 rounded-lg">
              <FileCode className="text-red-500" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold">Upload Discovery Script</h2>
              <p className="text-xs text-gray-400 mt-0.5">{packageName || 'Package'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={uploading}
            className="p-2 rounded-full hover:bg-red-900/30 transition-colors disabled:opacity-50"
          >
            <X className="text-gray-400 hover:text-white" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-5">
          <div className="bg-[#252525] rounded-lg p-4 border border-gray-700">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-500">Package Name:</span>
                <p className="text-white font-medium mt-0.5">{packageName || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500">Package Identifier:</span>
                <p className="text-red-400 font-mono text-xs mt-1 break-all">{packageIdentifier || '-'}</p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Script File <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              accept=".ps1,.sh,.bat,.cmd"
              disabled={uploading}
              className="w-full px-4 py-3 bg-[#2a2a2a] text-white rounded-lg border border-gray-600 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-red-600 file:text-white file:text-xs file:font-medium hover:file:bg-red-700 file:cursor-pointer cursor-pointer"
            />
            {selectedFile && (
              <p className="text-xs text-gray-500 mt-1.5">
                Selected: <span className="text-green-400">{selectedFile.name}</span>
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 mt-6 pt-5 border-t border-gray-700">
          <button
            onClick={onClose}
            disabled={uploading}
            className="px-5 py-2.5 border border-gray-600 rounded-lg text-gray-300 hover:bg-gray-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            className="px-5 py-2.5 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 rounded-lg text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg shadow-red-500/20 font-medium"
          >
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload size={18} />
                Upload
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadDiscoveryScriptModal;
